import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from '../../state/hooks';
import { IncidentCreationViewServices } from './viewServices/createIncident.view.services';
import { IncidentProps } from './viewServices/createIncident.view.slice';
import {
  ConneqtRepository,
  IncidentRepository,
  PlayerRepository,
  TeamRepository,
} from '../../state/reduxRepositories';
import { IncidentStepperService } from './incidentStepper.queryParam.service';
import { IncidentStepperQuery } from './incidentStepper.interface';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';
import { store } from '../../state/store';
import { Conneqt } from '../../state/reduxRepositories/conneqt/conneqt.interfaces';
import { IncidentPlayerGuardian } from './smart/incidentCreatedSuccessfully.interface';
import { CreateIncidentQueryService } from './createIncident.queryParam.service';
import { CreateIncidentPageQuery } from './createIncident.queryParams.interface';
import { GlobalQueryParams } from '../../state/queryParams/queryParams.interface';
import { Incident } from '../../state/reduxRepositories/incident/incident.interface';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { LoggedInUser } from '../../state/helperServices/auth/auth.interfaces';
import { Team } from '../../state/reduxRepositories/teams/team.interfaces';

@injectable()
export class CreateIncidentDomainService {
  currentPlayerSelector: any;
  playersGuardianSelector: any;
  orgTeamPlayerSelector: any;

  validateIncidentDateTimeSelector: any;

  public constructor(
    private incidentCreationViewService: IncidentCreationViewServices,
    private playerRepository: PlayerRepository,
    private incidentStepperService: IncidentStepperService,
    private incidentRepository: IncidentRepository,
    private createIncidentQueryService: CreateIncidentQueryService,
    private conneQtRepository: ConneqtRepository,
    private authService: AuthService,
    private teamRepository: TeamRepository,
  ) {
    this.createCurrentPlayerSelector();
    this.createPlayersGuardianSelector();
    this.createIncidentDateTimeValidationSelector();
    this.createOrgTeamPlayerSelector();
  }

  useIncidentCreateViewSelector = () => {
    return useAppSelector(
      this.incidentCreationViewService.incidentCreationViewSelector,
    );
  };

  usePlayerResourceSelector = () => {
    return this.playerRepository.useResourcesSelector();
  };

  useOrgTeamPlayerSelector = (): Player[] => {
    return useAppSelector(this.orgTeamPlayerSelector);
  };

  dispatchIncidentCreation = (incident: IncidentProps) => {
    this.incidentCreationViewService.dispatchIncidentCreationView(incident);
  };

  dispatchQueryParams = (
    params: CreateIncidentPageQuery & GlobalQueryParams,
  ) => {
    this.createIncidentQueryService.dispatchQueryParams(params);
  };

  dispatchCreateIncident = (incidentData: any) => {
    this.incidentRepository.dispatchCreate(incidentData);
  };

  dispatchClearIncidentCreationView = () => {
    return this.incidentCreationViewService.dispatchClearIncidentCreationView();
  };

  useIncidentQueryParamsSelector = () => {
    return this.createIncidentQueryService.useSelectMergedQuery();
  };

  useActiveStepSelector = () => {
    return this.incidentStepperService.useQuerySelector().activeStep;
  };

  useSelectedPlayerSelector = (): Player => {
    return useAppSelector(this.currentPlayerSelector);
  };

  useIncidentDateTimeValidationSelector = (): boolean => {
    return useAppSelector(this.validateIncidentDateTimeSelector);
  };

  usePlayersGuardianSelector = (): IncidentPlayerGuardian[] => {
    return useAppSelector(this.playersGuardianSelector);
  };

  dispatchNextPageQuery = () => {
    const queryParams =
      this.incidentStepperService.incidentStepperQuerySelector(
        store.getState(),
      );

    const { activeStep } = queryParams;
    let step = activeStep || 0;
    // eslint-disable-next-line no-return-assign
    return this.incidentStepperService.dispatchQueryParams({
      ...queryParams,
      activeStep: step ? (step += 1) : 1,
    });
  };

  dispatchSkipPlayerPageQuery = () => {
    return this.incidentStepperService.dispatchQueryParams({
      activeStep: 1,
    });
  };

  dispatchPreviousPageQuery = () => {
    const queryParams =
      this.incidentStepperService.incidentStepperQuerySelector(
        store.getState(),
      );
    const { activeStep } = queryParams;
    let step = activeStep;
    // eslint-disable-next-line no-return-assign
    return this.incidentStepperService.dispatchQueryParams({
      ...queryParams,
      activeStep: step ? (step -= 1) : 0,
    });
  };

  dispatchResetStep = () => {
    this.incidentStepperService.dispatchQueryParams({
      activeStep: 0,
    });
  };

  dispatchSetSelectedPlayerToEmpty = () => {
    this.createIncidentQueryService.dispatchQueryParams({
      selectedPlayerId: null,
    });
  };

  private getIncidentMaximumDatePerPlayer = (
    incidents: any,
    playerId?: string,
  ) => {
    const filter = incidents.filter(
      (incident: Incident) => incident.playerId === playerId,
    );
    const maxDate = Math.max(
      ...filter.map((e: any) => {
        const toLocaleStringDate = new Date(
          e.incidentDateTime,
        ).toLocaleString();
        return new Date(toLocaleStringDate);
      }),
    );
    return new Date(maxDate).getTime();
  };

  private createIncidentDateTimeValidationSelector = () => {
    this.validateIncidentDateTimeSelector = createSelector(
      this.createIncidentQueryService.mergedQuerySelector,
      this.incidentCreationViewService.incidentCreationViewSelector,
      this.playerRepository.selectors.resourcesSelector,
      (queryParams, incidentViewService: IncidentProps, players: Player[]) => {
        const playerClearedToPlayUpdatedAt = new Date(
          players.find((player) => player.id === queryParams.selectedPlayerId)
            ?.clearedToPlayUpdatedAt as string,
        ).toLocaleString();
        const dateNow = new Date().toLocaleString();
        const selectedIncidentDateTime = new Date(
          incidentViewService?.incidentDateTime as string,
        ).toLocaleString();
        if (
          new Date(selectedIncidentDateTime).getTime() >
          new Date(dateNow).getTime()
        ) {
          return false;
        }

        if (
          new Date(playerClearedToPlayUpdatedAt).getTime() >
          new Date(selectedIncidentDateTime).getTime()
        ) {
          return false;
        }
        return true;
      },
    );
  };

  private createPlayersGuardianSelector = () => {
    this.playersGuardianSelector = createSelector(
      this.createIncidentQueryService.mergedQuerySelector,
      this.conneQtRepository.selectors.resourcesSelector,
      (queryParams, conneQts: Conneqt[]) => {
        const conneQtionFromOrgToGuardian = conneQts.filter(
          (conneQt) =>
            conneQt.responsePlayerId === queryParams.selectedPlayerId,
        );

        const conneQtionFromGuardianToClinic = conneQts.filter(
          (conneQt) => conneQt.requestPlayerId === queryParams.selectedPlayerId,
        );

        const filterConneQts =
          conneQtionFromOrgToGuardian?.length > 0
            ? conneQtionFromOrgToGuardian
            : conneQtionFromGuardianToClinic;

        return filterConneQts.map((filterConneQt: Conneqt) => {
          return {
            responseEmail: filterConneQt?.responseEmail,
            responseAccountType: filterConneQt?.responseAccountType,
            requestAccountType: filterConneQt?.requestAccountType,
            requestAccountEmail: filterConneQt?.requestAccountEmail,
          };
        });
      },
    );
  };

  private createCurrentPlayerSelector = () => {
    this.currentPlayerSelector = createSelector(
      this.createIncidentQueryService.mergedQuerySelector,
      this.playerRepository.selectors.resourcesSelector,
      (queryParams, players: Player[]) => {
        return players.find(
          (a: Player) => a.id === queryParams.selectedPlayerId,
        );
      },
    );
  };

  createOrgTeamPlayerSelector = () => {
    this.orgTeamPlayerSelector = createSelector(
      this.playerRepository.selectors.resourcesSelector,
      this.authService.loggedInUserSelector,
      this.teamRepository.selectors.resourcesSelector,
      (players: Player[], user: LoggedInUser, teams: Team[]) => {
        const orgUserTeams = teams.filter(
          (team) =>
            team.teamManagerUserIds.includes(user.id) &&
            team.playerIds.length > 0,
        );

        const mapOrgPlayersByTeams = orgUserTeams.map((orgUserTeam: Team) => {
          return players.filter((player) =>
            orgUserTeam.playerIds.includes(player.id),
          );
        });

        const allTeamPlayers = mapOrgPlayersByTeams.flat(2);

        const allTeamPlayersDeDuplicated = players.filter((p) =>
          allTeamPlayers.find((teamPlayer) => p.id === teamPlayer.id),
        );

        return allTeamPlayersDeDuplicated;
      },
    );
  };
}
